import { Input, Text } from '@components/ui';
import {
  allyFilterLabelKey,
  allyFilters,
  defaultJobListFilter,
  JobKindChip,
  type JobListFilter,
  jobListFilterIsActive,
  jobListTitle,
  jobScopeLabelKey,
  jobScopes,
  retailFilterLabelKey,
  retailFiltersForConfig,
  sanitizeJobListFilter,
} from '@features/jobs';
import { useAvailableJobsQuery, useCurrentJobsQuery } from '@lib/api/jobs';
import { FEATURE_FLAG_KEYS, useFeatureFlag } from '@lib/featureFlags';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useDeferredValue, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import type { R8trJob } from './r8trJobsTypes';

export type R8trJobListTab = 'available' | 'current';

export type R8trJobFiltersByTab = Record<R8trJobListTab, JobListFilter>;

export const defaultR8trJobFiltersByTab = (): R8trJobFiltersByTab => ({
  available: defaultJobListFilter,
  current: defaultJobListFilter,
});

interface R8trJobsProps {
  tab: R8trJobListTab;
  onTabChange: (tab: R8trJobListTab) => void;
  filters: R8trJobFiltersByTab;
  onFiltersChange: (filters: R8trJobFiltersByTab) => void;
}

/** Matches API R8TR sort: value UI is pay (buyerCost), what the list shows. */
type JobSort = 'distance' | 'pay';

type JobSortsByTab = Record<R8trJobListTab, JobSort>;
type JobSearchesByTab = Record<R8trJobListTab, string>;

const pillSx = (selected: boolean) => ({
  flex: '0 0 auto',
  border: 0,
  cursor: 'pointer',
  borderRadius: 999,
  px: 1.5,
  py: 0.75,
  font: 'inherit',
  fontWeight: 600,
  fontSize: 13,
  fontVariantNumeric: 'tabular-nums',
  textAlign: 'center',
  whiteSpace: 'nowrap',
  color: selected ? 'text.primary' : 'text.secondary',
  bgcolor: selected ? 'background.paper' : 'transparent',
  boxShadow: selected ? '0 1px 2px rgba(0,0,0,0.12)' : 'none',
});

const pillTrackSx = {
  gap: 0.5,
  p: 0.5,
  borderRadius: 999,
  bgcolor: 'action.hover',
};

const segmentedSx = {
  ...pillTrackSx,
  display: 'flex',
  flexWrap: 'wrap',
  width: 'max-content',
  maxWidth: '100%',
  boxSizing: 'border-box',
};

const jobsForTab = (
  tab: R8trJobListTab,
  availableJobs: R8trJob[],
  currentJobs: R8trJob[]
): R8trJob[] => {
  switch (tab) {
    case 'available':
      return availableJobs;
    case 'current':
      return currentJobs;
    default: {
      const unreachable: never = tab;
      return unreachable;
    }
  }
};

const R8trJobs = ({
  tab,
  onTabChange,
  filters,
  onFiltersChange,
}: R8trJobsProps): JSX.Element => {
  const { t } = useTranslation();
  const navigator = useNavigate();
  const [sorts, setSorts] = useState<JobSortsByTab>({
    available: 'distance',
    current: 'distance',
  });
  const [searches, setSearches] = useState<JobSearchesByTab>({
    available: '',
    current: '',
  });
  const search = searches[tab];
  const sort = sorts[tab];
  const deferredAvailableSearch = useDeferredValue(searches.available).trim();
  const deferredCurrentSearch = useDeferredValue(searches.current).trim();
  const deferredSearch =
    tab === 'available' ? deferredAvailableSearch : deferredCurrentSearch;
  const fallbackTitle = t('jobDetail.fallbackTitle');
  const nonAutomotiveEnabled = useFeatureFlag(
    FEATURE_FLAG_KEYS.NON_AUTOMOTIVE_CATEGORY
  );
  const availableFilter = sanitizeJobListFilter(
    filters.available,
    nonAutomotiveEnabled
  );
  const currentFilter = sanitizeJobListFilter(
    filters.current,
    nonAutomotiveEnabled
  );
  const activeFilter = tab === 'available' ? availableFilter : currentFilter;
  const visibleRetailFilters = retailFiltersForConfig(nonAutomotiveEnabled);
  const { data: availableJobsResult = [] } = useAvailableJobsQuery(
    availableFilter,
    {
      sort: sorts.available,
      search: deferredAvailableSearch,
    }
  );
  const { data: currentJobsResult = [] } = useCurrentJobsQuery(currentFilter, {
    sort: sorts.current,
    search: deferredCurrentSearch,
  });
  const availableJobsData = availableJobsResult as R8trJob[];
  const currentJobsData = currentJobsResult as R8trJob[];
  const jobs = jobsForTab(tab, availableJobsData, currentJobsData);
  const emptyMessage =
    jobs.length === 0 && deferredSearch
      ? t('jobs.emptySearch', { query: search.trim() })
      : jobs.length === 0 && jobListFilterIsActive(activeFilter)
        ? t('jobs.emptyFilter')
        : t('jobs.empty');
  const subtype =
    activeFilter.scope === 'ALLY'
      ? allyFilters
      : activeFilter.scope === 'RETAIL'
        ? visibleRetailFilters
        : [];

  const setTabFilter = (next: JobListFilter): void => {
    onFiltersChange({ ...filters, [tab]: next });
  };

  const setTabSearch = (next: string): void => {
    setSearches((current) => ({ ...current, [tab]: next }));
  };

  const setTabSort = (next: JobSort): void => {
    setSorts((current) => ({ ...current, [tab]: next }));
  };

  useEffect(() => {
    const tabFilter = filters[tab];
    if (tabFilter.retail === 'NON_AUTOMOTIVE' && !nonAutomotiveEnabled) {
      onFiltersChange({
        ...filters,
        [tab]: sanitizeJobListFilter(tabFilter, false),
      });
    }
  }, [filters, nonAutomotiveEnabled, onFiltersChange, tab]);

  return (
    <Box
      sx={{
        overflow: 'hidden',
        border: 1,
        borderColor: 'divider',
        borderRadius: 3,
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Tabs
          value={tab}
          onChange={(_event, value: R8trJobListTab) => onTabChange(value)}
          sx={{
            minHeight: 40,
            '& .MuiTab-root': {
              minHeight: 40,
              px: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              color: 'text.secondary',
            },
            '& .Mui-selected': { color: 'text.primary' },
          }}
        >
          <Tab
            value="available"
            label={`${t('jobs.available')} (${availableJobsData.length})`}
          />
          <Tab
            value="current"
            label={`${t('jobs.current')} (${currentJobsData.length})`}
          />
        </Tabs>
        <Box sx={{ ...pillTrackSx, display: 'flex', flexShrink: 0 }}>
          <Box
            component="button"
            type="button"
            sx={pillSx(sort === 'distance')}
            onClick={() => setTabSort('distance')}
          >
            {t('jobs.sortDistance')}
          </Box>
          <Box
            component="button"
            type="button"
            sx={pillSx(sort === 'pay')}
            onClick={() => setTabSort('pay')}
          >
            {t('jobs.sortValue')}
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 1.5,
          px: 2,
          py: 1.25,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box role="search" sx={{ flex: 1, minWidth: 0 }}>
          <Input
            type="text"
            value={search}
            onChange={(event) => setTabSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') setTabSearch('');
            }}
            placeholder={t('jobs.searchPlaceholder')}
            inputProps={{
              'aria-label': t('jobs.search'),
              enterKeyHint: 'search',
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon
                    sx={{ fontSize: 20, color: 'text.secondary' }}
                  />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    edge="end"
                    aria-label={t('jobs.clearSearch')}
                    onClick={() => setTabSearch('')}
                  >
                    <CloseRoundedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>
        <Box sx={segmentedSx}>
          {jobScopes.map((scope) => {
            const selected = activeFilter.scope === scope;
            const label = selected
              ? `${t(jobScopeLabelKey(scope))} (${jobs.length})`
              : t(jobScopeLabelKey(scope));
            return (
              <Box
                key={scope}
                component="button"
                type="button"
                title={label}
                aria-pressed={selected}
                onClick={() => setTabFilter({ ...defaultJobListFilter, scope })}
                sx={pillSx(selected)}
              >
                {label}
              </Box>
            );
          })}
        </Box>
      </Box>
      {subtype.length > 0 ? (
        <Box
          sx={{
            px: 2,
            py: 1.25,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box sx={segmentedSx}>
            {activeFilter.scope === 'ALLY'
              ? allyFilters.map((ally) => {
                  const selected = activeFilter.ally === ally;
                  const label = selected
                    ? `${t(allyFilterLabelKey(ally))} (${jobs.length})`
                    : t(allyFilterLabelKey(ally));
                  return (
                    <Box
                      key={ally}
                      component="button"
                      type="button"
                      title={label}
                      aria-pressed={selected}
                      onClick={() => setTabFilter({ ...activeFilter, ally })}
                      sx={pillSx(selected)}
                    >
                      {label}
                    </Box>
                  );
                })
              : visibleRetailFilters.map((retail) => {
                  const selected = activeFilter.retail === retail;
                  const label = selected
                    ? `${t(retailFilterLabelKey(retail))} (${jobs.length})`
                    : t(retailFilterLabelKey(retail));
                  return (
                    <Box
                      key={retail}
                      component="button"
                      type="button"
                      title={label}
                      aria-pressed={selected}
                      onClick={() => setTabFilter({ ...activeFilter, retail })}
                      sx={pillSx(selected)}
                    >
                      {label}
                    </Box>
                  );
                })}
          </Box>
        </Box>
      ) : null}
      {jobs.length > 0 ? (
        jobs.map((item) => {
          const title = jobListTitle(item, fallbackTitle);
          return (
            <Box
              key={item.id}
              component="button"
              type="button"
              onClick={() =>
                navigator('/detail', { state: { jobId: item.id } })
              }
              sx={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.75,
                border: 0,
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'transparent',
                cursor: 'pointer',
                textAlign: 'left',
                font: 'inherit',
                color: 'inherit',
                '&:last-of-type': { borderBottom: 0 },
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Box
                component="span"
                sx={{
                  display: 'flex',
                  height: 44,
                  width: 44,
                  flexShrink: 0,
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  borderRadius: 2,
                  bgcolor: 'action.hover',
                }}
              >
                {item.category?.icon ? (
                  <Box
                    component="img"
                    src={item.category.icon}
                    alt=""
                    sx={{ height: 28, width: 28, objectFit: 'contain' }}
                  />
                ) : null}
              </Box>
              <Box component="span" sx={{ minWidth: 0, flex: 1 }}>
                <Box
                  component="span"
                  sx={{
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontWeight: 600,
                  }}
                >
                  {title}
                </Box>
                <Box
                  component="span"
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 0.75,
                    mt: 0.5,
                  }}
                >
                  <Box
                    component="span"
                    sx={{ fontSize: 13, color: 'text.secondary' }}
                  >
                    {item.distance?.toFixed(1)} {t('jobs.miles')}
                  </Box>
                  <JobKindChip job={item} />
                </Box>
              </Box>
              <Box
                component="span"
                sx={{ flexShrink: 0, fontWeight: 700, color: 'text.primary' }}
              >
                ${Number(item.buyerCost / 2).toLocaleString('en-US')}
              </Box>
            </Box>
          );
        })
      ) : (
        <Text
          variant="body"
          tone="muted"
          sx={{ display: 'block', px: 2, py: 4 }}
        >
          {emptyMessage}
        </Text>
      )}
    </Box>
  );
};

export default R8trJobs;
